/**
 * Optimized cache configuration for better performance
 */

// Optimized cache times for better performance
export const CACHE_TIMES = {
  // Static data that changes infrequently
  STATIC: {
    staleTime: 15 * 60 * 1000, // 15 minutes (increased for better caching)
    gcTime: 30 * 60 * 1000, // 30 minutes (increased for better memory efficiency)
  },

  // Dynamic data that changes moderately
  DYNAMIC: {
    staleTime: 5 * 60 * 1000, // 5 minutes (increased from 1)
    gcTime: 15 * 60 * 1000, // 15 minutes (increased from 5)
  },

  // Real-time data that changes frequently
  REALTIME: {
    staleTime: 15 * 1000, // 15 seconds (reduced from 30)
    gcTime: 1 * 60 * 1000, // 1 minute (reduced from 2)
  },

  // Critical data that should always be fresh
  CRITICAL: {
    staleTime: 0, // Always fresh
    gcTime: 30 * 1000, // 30 seconds (reduced from 1 minute)
  },
} as const;

// Optimized refetch configurations
export const REFETCH_CONFIG = {
  // Standard background refetching
  STANDARD: {
    refetchOnMount: true,
    refetchOnWindowFocus: false, // Disabled for better performance
    refetchOnReconnect: true,
  },

  // Aggressive refetching for critical data
  AGGRESSIVE: {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 2 * 60 * 1000, // 2 minutes (reduced from 1)
  },

  // Minimal refetching for static data
  MINIMAL: {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  },
} as const;

// Optimized query key factories
export const QUERY_KEYS = {
  // User-related queries
  user: {
    profile: (userId: string) => ["user", "profile", userId],
    profileOptimized: (userId: string) => ["user", "profileOptimized", userId],
    role: (userId: string) => ["user", "role", userId],
    wallet: (userId: string) => ["user", "wallet", userId],
    profileLike: (userId: string, profileId: string) => [
      "profileLike",
      userId,
      profileId,
    ],
  },

  // Track-related queries
  tracks: {
    all: () => ["tracks"],
    list: (filters?: Record<string, any>) => ["tracks", "list", filters],
    detail: (trackId: string) => ["tracks", "detail", trackId],
    byArtist: (artistId: string) => ["tracks", "artist", artistId],
    bySponsor: (sponsorId: string) => ["tracks", "sponsor", sponsorId],
    favorites: (userId: string, trackId: string) => [
      "tracks",
      "favorites",
      userId,
      trackId,
    ],
  },

  // Admin-related queries
  admin: {
    users: () => ["admin", "users"],
    activities: (page: number, limit: number) => [
      "admin",
      "activities",
      page,
      limit,
    ],
    tracks: () => ["admin", "tracks"],
    cashouts: () => ["admin", "cashouts"],
    verifications: () => ["admin", "verifications"],
    sponsorships: () => ["admin", "sponsorships"],
    transactions: () => ["admin", "transactions"],
    systemStatus: () => ["admin", "systemStatus"],
  },

  // Statistics queries
  stats: {
    platform: () => ["stats", "platform"],
    user: (userId: string) => ["stats", "user", userId],
    artist: (artistId: string) => ["stats", "artist", artistId],
    sponsor: (sponsorId: string) => ["stats", "sponsor", sponsorId],
  },
} as const;

// Performance monitoring helpers
export const PERFORMANCE_CONFIG = {
  // Enable query devtools only in development
  devtools: process.env.NODE_ENV === "development",

  // Default retry configuration
  retry: {
    attempts: 2, // Reduced from 3
    delay: 1000, // 1 second initial delay
  },

  // Network mode configuration
  networkMode: "online" as const,

  // Error boundary configuration
  throwOnError: false,
} as const;
